import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { useGlobalContext } from 'GlobalContext';
import { useSettings } from 'services/settings/SettingsContext';
import { useAuthContext } from 'services/authentication/AuthenticationContext';
import { useModal } from 'components/modals/ModalContext';
import { useAllowedRight } from 'services/permissions/permissionChecks';
import { v4 as uuidv4 } from 'uuid';
import { fetchDataAndOptionsOfDetailCards } from 'services/api/fetchFieldData';
import { FieldType, FieldOptionFetchResponse, FieldOption, EditableStatusType } from 'types/FieldTypes';
import { DetailPageProps, DetailPageItemId } from 'types/DetailPageTypes';
import generateHeaderButtons from 'components/modals/functions/generateHeaderButtons';
import LiveEditField from 'components/forms/LiveEditField';
import AssetsCard from 'components/detailcards/AssetsCard';
import ContractRevenueCard from '../../views/contracts/ContractRevenueCard';
import RelatedOrganizationsCard from 'components/detailcards/RelatedOrganizationsCard';
import PersonOrganizationCard from 'components/detailcards/PersonOrganizationCard';
import FieldsCard from 'components/detailcards/FieldsCard';
import PeopleCard from 'components/detailcards/PeopleCard';
import { faAngleLeft } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import '../../style/scss/detailpage.scss';
import { isArrayOfConditions } from 'services/utils/isArrayOfConditions';
import StatusBar from './StatusBar';
import ContactCard from 'components/detailcards/ContactCard';
import LinkedDocumentsCard from 'components/detailcards/LinkedDocumentsCard';
import TabBar from './TabBar2';
import { useFetchContext } from 'services/api/FetchContext';

const DetailPage: React.FC<DetailPageProps> = ({ 
    apiObject, pageObject, headerTitle, headerTitleDataField, headerTranslationString, headerTitleFields, 
    headerButtons, statusBar, detailCards, pageIcon, headerParent, headerParents, tabs, backButtonUrl
}) => {
    const { t } = useTranslation();
    const history = useHistory();
    const viewKey = useMemo(() => uuidv4(), []);
    const { id } = useParams<DetailPageItemId>();
    const { setFloatingAlert } = useGlobalContext();
    const { userHash, activeFeatures, roleFeatures, allowedFeatures, allowedRights, handleLogout } = useAuthContext();
    const { userLocale, userTimezone } = useSettings();
    const { getCache, setCache } = useFetchContext();
    const { initializeModal } = useModal();
    const hasRightCheck = useAllowedRight;
    const itemId = id;
    const [data, setData] = useState<FieldType[]>([]);
    const [statuses, setStatuses] = useState(null);
    const [currentStatus, setCurrentStatus] = useState<number | null>(null);
    const [resolution, setResolution] = useState<string>('');
    const [header, setHeader] = useState<string | null>(null);
    const [loadStatus, setLoadStatus] = useState<"idle" | "loading" | "loaded">("idle");
    const [reloadTabs, setReloadTabs] = useState(0);
    const [dropdownData, setDropdownData] = useState<Record<string, FieldOptionFetchResponse>>({});
    const [groupOptions, setGroupOptions] = useState<FieldOption[]>([])
    const [buttonLoader, setButtonLoader] = useState<Record<string, boolean>>({});
    const [editable] = useState<EditableStatusType>({ editability: !hasRightCheck('only_view') });

    // Fetch the data of the fields and dropdown options of the page 
    useEffect(() => {
        setLoadStatus("idle");
        fetchData();
        // eslint-disable-next-line
    }, [itemId]);

    // Function to fetch the data to be used on initial render and for refetches
    const fetchData = async () => {
        const fetchedData = await fetchDataAndOptionsOfDetailCards(
            apiObject, 
            itemId, 
            dropdownData, 
            groupOptions, 
            headerButtons,
            detailCards, 
            activeFeatures,
            setData, 
            setDropdownData, 
            setGroupOptions, 
            setLoadStatus,
            getCache,
            setCache,
            handleLogout,
            setHeader,
            headerTitleDataField
        );

        if (!fetchedData) return;

        // Get the fetched data of the object
        const [fetchedObjectData] = fetchedData;

        // Set the statuses from the received data
        if (statusBar) {
            // Determine the api fields to get the data from
            const workflowField = statusBar.apiFields?.workflowField ?? 'workflow';
            const statusesField = statusBar.apiFields?.statusesField ?? 'statuses';
            const statusField = statusBar.apiFields?.statusField ?? 'status';
            const resolutionField = statusBar.apiFields?.resolutionField ?? '';

            // Set the status data
            setStatuses(fetchedObjectData[workflowField][statusesField]);
            setCurrentStatus(fetchedObjectData[statusField]);
            setResolution(fetchedObjectData[resolutionField])
        };

        // Return the fetched data
        return fetchedData;
    }

    // Handle the refetch if needed
    const handleRefetch = async () => {
        await fetchData();
        setReloadTabs(prev => prev + 1);
    };

    // Gives the loader a delay to smoothen fast transactions
    useEffect(() => {
        if (loadStatus === "idle") {
            const timer = setTimeout(() => {
                setLoadStatus("loading")
            }, 250);

            return () => {
                clearTimeout(timer);
            };
        }
    }, [loadStatus]);

    // Generates the card components
    const generateCardComponent = (componentType: string, fields: FieldType[], title: string | undefined, itemId: string, pageObject: string, viewKey: string, data: any, apiField: string | undefined, alignment: 'horizontal' | 'stacked' | undefined, dataCondition?: { [key: string]: any }[]) => {
        // If a data condition is set for the card, only render it if the corresponding data object has this value
        if (dataCondition && data) {
            const shouldDisplayCard = dataCondition.every((condition: { [key: string]: any }) => {
                // Evaluate a field based condition which requires a non empty value or list
                if (condition.field && condition.notEmpty) {
                    const fieldValue = data[condition.field];
                    return Array.isArray(fieldValue) ? fieldValue.length > 0 : Boolean(fieldValue);
                }

                // Evaluate a plain key value condition
                return Object.entries(condition).every(([key, value]) => data[key] === value)
            });

            if (!shouldDisplayCard) {
                return null;
            }
        }

        switch (componentType) {
            case 'FieldsCard':
                return <FieldsCard 
                            apiObject={pageObject} 
                            itemId={itemId} 
                            viewKey={viewKey} 
                            title={title} 
                            fields={fields} 
                            data={data} 
                            dropdownData={dropdownData} 
                            groupOptions={groupOptions} 
                            alignment={alignment} 
                            loadStatus={loadStatus} 
                            refetchData={handleRefetch} />
            case 'PeopleCard':
                return <PeopleCard 
                            apiObject={pageObject} 
                            itemId={itemId} 
                            viewKey={viewKey} 
                            personIds={apiField ? data[apiField] : undefined} 
                            loadStatus={loadStatus} 
                            isEditable={editable}
                            refetchData={() => handleRefetch()}
                        />
            case 'ContactCard':
                return <ContactCard
                            itemId={itemId} 
                            viewKey={viewKey} 
                            apiObject={apiObject}
                            loadStatus={loadStatus} 
                            data={data}
                            isEditable={editable}
                            refetchData={() => handleRefetch()}
                        />
            case 'LinkedDocumentsCard':
                return <LinkedDocumentsCard
                            itemId={itemId}
                            viewKey={viewKey}
                            apiObject={apiObject}
                            loadStatus={loadStatus}
                            data={data}
                            isEditable={editable}
                            refetchData={() => handleRefetch()}
                        />
            case 'ContractAssetsCard':
                return <AssetsCard
                            data={data}
                            loadStatus={loadStatus}
                            source='contract'
                        />
            case 'ContractRevenueCard':
                return <ContractRevenueCard
                            data={data}
                            loadStatus={loadStatus}
                        />
            case 'LocationAssetsCard':
                return <AssetsCard
                            data={data}
                            loadStatus={loadStatus}
                            source='location'
                            itemId={itemId}
                            isEditable={editable}
                        />
            case 'RelatedOrganizationsCard':
                return <RelatedOrganizationsCard
                            itemId={itemId}
                            data={data}
                            loadStatus={loadStatus}
                            isEditable={editable.editability}
                            refetchData={() => handleRefetch()}
                        />
            case 'PersonOrganizationCard':
                return <PersonOrganizationCard
                            itemId={itemId}
                            data={data}
                            loadStatus={loadStatus}
                            isEditable={editable.editability}
                            refetchData={() => handleRefetch()}
                        />
            // Plug in other card components
            default:
                return null;
        }
    }

    // Get the parent object of the header, the data is a plain object at runtime
    const headerParentObject = headerParent ? (data as any)?.[headerParent.dataField] : null;

    // Build the breadcrumb items shown in front of the title, from the single parent or the parents function
    const headerParentItems = useMemo(() => {
        if (headerParents && loadStatus === 'loaded') {
            return headerParents(data);
        }
        if (headerParent && headerParentObject?.id) {
            return [{
                key: `${headerParent.dataField}-${headerParentObject.id}`,
                label: headerParentObject[headerParent.labelField],
                url: `${headerParent.urlPrefix}${headerParentObject.id}`,
                icon: headerParent.icon,
            }];
        }
        return [];
    }, [headerParents, headerParent, headerParentObject, data, loadStatus]);

    // Resolve the page icon, it may depend on the loaded data
    const resolvedPageIcon = typeof pageIcon === 'function' ? (loadStatus === 'loaded' ? pageIcon(data) : null) : pageIcon;

    // Generate the header buttons
    const generatedHeaderButtons = data && headerButtons && itemId ? generateHeaderButtons(
        headerButtons, 
        pageObject, 
        data, 
        dropdownData, 
        parseInt(itemId), 
        initializeModal, 
        buttonLoader, 
        setButtonLoader, 
        setFloatingAlert,
        handleLogout,
        { activeFeatures, roleFeatures, allowedFeatures, allowedRights },
        userTimezone,
        userLocale,
        t,
        undefined,
        handleRefetch,
        undefined,
        history,
        userHash
    ) : null;

    return (
        <div className='detailpage container-filled'>
            <div className='detailpage-header row'>
                <div className='header-title'>
                    <span className='back-button'
                          onClick={() => {
                                if (backButtonUrl) {
                                    history.push(backButtonUrl);
                                } else {
                                    history.goBack();
                                }
                            }}>
                        <FontAwesomeIcon icon={faAngleLeft} />
                    </span>
                    {headerParentItems.map(parentItem => (
                        // Render the parent objects in front of the title, clicking one opens its detail page
                        <span className='header-parent' key={parentItem.key}>
                            {parentItem.icon && (
                                <span className='page-icon'>
                                    {parentItem.icon}
                                </span>
                            )}
                            <h2 className='fixed-title header-parent-link'
                                onClick={() => history.push(parentItem.url)}>
                                {parentItem.label}
                            </h2>
                            <span className='header-parent-separator'>/</span>
                        </span>
                    ))}
                    {resolvedPageIcon && (
                        <span className='page-icon'>
                            {resolvedPageIcon}
                        </span>
                    )}
                    {headerTitle && (
                        <h2 className='fixed-title'>{t(headerTitle)}</h2>
                    )}
                    {headerTitleDataField && !headerTranslationString && (
                        <h2 className='fixed-title'>{header}</h2>
                    )}
                    {headerTitleDataField && headerTranslationString && (
                        <h2 className='fixed-title'>{t(`${headerTranslationString}`, { value: t(header) } )}</h2>
                    )}
                    {headerTitleFields?.map((field, index) =>
                        <LiveEditField
                            key={index}
                            viewKey={viewKey}
                            apiObject={pageObject}
                            fieldItem={field}
                            itemId={itemId}
                            data={data} 
                            refetchData={handleRefetch}
                        />
                    )}
                </div>
                {headerButtons && 
                    // Render the header buttons of the detail page
                    <div className='button-container right-align'>
                        {generatedHeaderButtons}
                    </div>
                }
            </div>
            {statusBar && statuses && (
                // Render the status bar of the detail modal
                <StatusBar statuses={statuses} 
                           currentStatus={currentStatus}
                           pageId={parseInt(itemId)}
                           resolution={resolution}
                           statusClickable={statusBar.changeStatusOnClick}
                           refetchData={fetchData}
                />
            )}
            <div className='detailpage-content'
                 style={statusBar ? { marginTop: '1.75rem'} : { marginTop: '2.5rem'}}>
                <div>
                    {detailCards.map((card) =>
                        <div key={`${card.title}-${card.component}`}>
                            {card.component &&
                                generateCardComponent(
                                    card.component, 
                                    card.fields || [], 
                                    card.title, 
                                    itemId, 
                                    pageObject, 
                                    viewKey, 
                                    data, 
                                    card.apiField, 
                                    card.alignment, 
                                    isArrayOfConditions(card.dataCondition) ? card.dataCondition : undefined
                                )
                            }
                        </div>
                    )}
                </div>
                <div>
                    {tabs && (
                        <TabBar
                            tabs={tabs} 
                            data={data}
                            viewKey={viewKey} 
                            itemId={id} 
                            pageContext={pageObject}
                            reloadTabs={reloadTabs}
                            refetchData={handleRefetch}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

export default DetailPage;